import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import type {PayloadAction} from '@reduxjs/toolkit'
import {createAction, createSlice, prepareAutoBatched} from '@reduxjs/toolkit'
import type {StartQueryActionCreatorOptions} from '@reduxjs/toolkit/query'

import {getPager} from '../components/common/Pager/Pager.selectors'
import type {Pager} from '../components/common/Pager/Pager.types'
import {PagerGroup} from '../components/common/Pager/Pager.types'
import {getLastPageToCount} from '../components/common/Pager/Pager.utils'
import {getBuildsArg, getBuildsCountArg, getBuildsDetailsArg} from '../rest/builds'
import {getBuildLocator} from '../rest/locators'
import type {NormalizedBuilds} from '../rest/schemata'
import {
  normalizeBuild,
  normalizeBuildChanges,
  normalizeBuilds,
  normalizeCompatibleAgents,
} from '../rest/schemata'
import type {LoadedEndpoint, LoadedEndpoints} from '../routes/loadEndpoint'
import {loadEndpoint} from '../routes/loadEndpoint'
import {getBuildsLocatorWithPage} from '../selectors'
import type {Build} from '../services/rest'
import {restApi} from '../services/rest'
import type {BuildId, NormalizedBuild, RequestOptionsParams} from '../types'
import {
  ActionThrottlerWithArrayCollect,
  ActionThrottlerWithObjectCollect,
} from '../utils/actionThrottler'
import {notNull, truthy} from '../utils/guards'
import {keyValue} from '../utils/object'
import type {Refetchable} from '../utils/refetchable'
import {combineRefetchables} from '../utils/refetchable'
import type {Unsubscribe} from '../utils/subscriber'
import {subscribeOnRunningBuild} from '../utils/subscriber'

import {buildDetailsQueue} from './buildDetailsQueue'
import type {AppThunk} from './types'

type ReceiveBuildThrottledParams = {
  withBuildTypes?: boolean
  locator?: string
}

export const receiveBuildAction = createAction(
  'receiveBuild',
  (data: ReadonlyArray<Build>, locators: string[], params?: ReceiveBuildThrottledParams) => ({
    payload: normalizeBuilds(data),
    meta: {arg: {originalArgs: {locators, ...params}}},
  }),
)

export const receiveBuild = (data: Build, locator: string = getBuildLocator(data.id)) =>
  receiveBuildAction([data], [locator])

const receiveBuildThrottled =
  (receiveBuildData: ReadonlyArray<Build>, params?: ReceiveBuildThrottledParams): AppThunk<any> =>
  dispatch => {
    if (receiveBuildData.length) {
      dispatch(
        receiveBuildAction(
          receiveBuildData,
          receiveBuildData.map(item => getBuildLocator(item.id)),
          params,
        ),
      )
    }
  }

const receiveBuildWithThrottle = new ActionThrottlerWithArrayCollect(
  builds =>
    receiveBuildThrottled(builds, {
      withBuildTypes: true,
    }),
  [],
)
export const multiplyReceiveBuild = (data: Build): AppThunk<any> =>
  receiveBuildWithThrottle.fetch([data])

export const receiveBuildWSDataAction =
  createAction<KeyValue<BuildId, NormalizedBuild | null>>('receiveBuildWSData')

const receiveBuildWSDataWithThrottle = new ActionThrottlerWithObjectCollect(
  receiveBuildWSDataAction,
  {},
)
export const receiveBuildWSData = (data: NormalizedBuild): AppThunk<any> =>
  receiveBuildWSDataWithThrottle.fetch(keyValue(data.id, data))
export const subscribeOnBuild =
  (buildId: BuildId): AppThunk<Unsubscribe> =>
  dispatch =>
    subscribeOnRunningBuild(buildId, data => {
      if (data == null) {
        return
      }

      const buildsWSData: KeyValue<BuildId, NormalizedBuild | null> | null | undefined =
        normalizeBuild(data).entities.builds

      if (buildsWSData != null) {
        dispatch(receiveBuildWSDataWithThrottle.fetch(buildsWSData))
      }
    })
export const compatibleAgents = createSlice({
  name: 'compatibleAgents',
  initialState: {},
  reducers: {
    receive: {
      reducer(state, action: PayloadAction<NormalizedBuilds>) {
        Object.assign(state, action.payload.entities.compatibleAgents)
      },
      prepare: prepareAutoBatched<NormalizedBuilds>(),
    },
  },
  extraReducers: builder => {
    builder.addCase(receiveBuildWSDataAction, (state, action) => {
      Object.assign(state, action.payload)
    })
  },
})
export const receiveCompatibleAgents = (data: ReadonlyArray<Build>) =>
  compatibleAgents.actions.receive(normalizeCompatibleAgents(data))
export const receiveBuildChanges = createAction(
  'receiveBuildChanges',
  (data: ReadonlyArray<Build>) =>
    prepareAutoBatched<NormalizedBuilds>()(normalizeBuildChanges(data)),
)
export const receiveBuildsDetails =
  (data: ReadonlyArray<Build>): AppThunk<any> =>
  dispatch => {
    dispatch(receiveBuildChanges(data))
    dispatch(receiveCompatibleAgents(data))
  }
export const stopFetching = (queueEntryId: string): void => buildDetailsQueue.cancel(queueEntryId)

const fetchBuildsDetails =
  (
    locatorToStore: string,
    locatorToFetch: string,
    initiateOptions: StartQueryActionCreatorOptions,
    requestOptions: RequestOptionsParams | null | undefined,
  ): AppThunk<LoadedEndpoint> =>
  dispatch => {
    const throttler = new ActionThrottlerWithArrayCollect(receiveBuildsDetails, [])
    return dispatch(
      loadEndpoint(
        restApi.endpoints.getAllBuildsDetailsNormalized,
        getBuildsDetailsArg(locatorToStore, locatorToFetch, requestOptions, build =>
          dispatch(throttler.fetch([build])),
        ),
        initiateOptions,
      ),
    )
  }

type FetchBuildsPageDataParams = {
  locator: string
  pager: Pager
  page?: number
  requestOptions?: RequestOptionsParams
  extraRequestOptions?: RequestOptionsParams
  inBackground?: boolean
}

export const DEFAULT_LOOKUP_LIMIT = 10000

type FetchBuildDataArg = {
  locator: string
  requestOptions?: RequestOptionsParams
  extraRequestOptions?: RequestOptionsParams
  inBackground?: boolean
}
const fetchBuildsAction =
  (
    args: FetchBuildDataArg,
    initiateOptions: StartQueryActionCreatorOptions,
  ): AppThunk<LoadedEndpoint> =>
  dispatch => {
    const {locator, requestOptions, inBackground} = args
    const throttler = new ActionThrottlerWithArrayCollect(
      builds => receiveBuildThrottled(builds, {locator}),
      [],
    )
    return dispatch(
      loadEndpoint(
        restApi.endpoints.getAllBuildsNormalized,
        getBuildsArg(locator, requestOptions, build => {
          if (!inBackground && requestOptions?.withProgress !== false) {
            dispatch(throttler.fetch([build]))
          }
        }),
        initiateOptions,
      ),
    )
  }

const fetchBuildsAllData =
  (
    locator: string,
    requestOptions: RequestOptionsParams = {},
    inBackground = false,
    initiateOptions: StartQueryActionCreatorOptions,
    extraRequestOptions?: RequestOptionsParams,
  ): AppThunk<LoadedEndpoints> =>
  (dispatch, getState) => {
    const {fetchCount} = requestOptions
    const {withBuildDetails, ...restRequestOptions} = requestOptions
    const state = getState()
    const result: LoadedEndpoints = []
    if (fetchCount != null) {
      const pager = getPager(state, PagerGroup.BUILD)
      result.push(
        dispatch(
          loadEndpoint(
            restApi.endpoints.getAllBuilds,
            getBuildsCountArg(
              locator,
              fetchCount,
              true,
              pager.lookupLimit ?? DEFAULT_LOOKUP_LIMIT,
              {options: restRequestOptions, inBackground},
            ),
            {...initiateOptions, forceRefetch: true},
          ),
        ),
      )
    }
    if (withBuildDetails) {
      result.push(
        dispatch(
          fetchBuildsDetails(locator, locator, initiateOptions, {
            ...restRequestOptions,
            essential: false,
          }),
        ),
      )
    }
    const request = dispatch(
      fetchBuildsAction(
        {locator, requestOptions: restRequestOptions, inBackground},
        initiateOptions,
      ),
    )
    result.push(request)

    if (extraRequestOptions) {
      result.push(
        dispatch(
          fetchBuildsAction(
            {locator, requestOptions: extraRequestOptions, inBackground},
            initiateOptions,
          ),
        ),
      )
    }
    return result
  }

const fetchBuildsPageData =
  (
    params: FetchBuildsPageDataParams,
    initiateOptions: StartQueryActionCreatorOptions,
  ): AppThunk<LoadedEndpoints> =>
  (dispatch, getState) => {
    const {
      locator,
      pager,
      requestOptions,
      extraRequestOptions,
      inBackground = false,
      page = 1,
    } = params
    const {withBuildDetails, ...restRequestOptions} = requestOptions ?? {}
    const {pageSize} = pager
    const locatorToStore = locator
    const state = getState()
    const lastPageToCount = getLastPageToCount(pager, page)
    const upperLimit = lastPageToCount * pageSize + 1
    const locatorToQuery = getBuildsLocatorWithPage(state, locator, page)

    return [
      dispatch(
        loadEndpoint(
          restApi.endpoints.getAllBuilds,
          getBuildsCountArg(locator, upperLimit, false, pager.lookupLimit ?? DEFAULT_LOOKUP_LIMIT, {
            options: restRequestOptions,
            inBackground,
            isPagerRequest: true,
          }),
          {...initiateOptions, forceRefetch: true},
        ),
      ),
      withBuildDetails
        ? dispatch(
            fetchBuildsDetails(locatorToStore, locatorToQuery, initiateOptions, {
              ...restRequestOptions,
              essential: false,
            }),
          )
        : null,
      dispatch(
        fetchBuildsAction(
          {locator: locatorToQuery, requestOptions: restRequestOptions, inBackground},
          initiateOptions,
        ),
      ),
      extraRequestOptions
        ? dispatch(
            fetchBuildsAction(
              {locator: locatorToQuery, requestOptions: extraRequestOptions, inBackground},
              initiateOptions,
            ),
          )
        : null,
    ]
  }

export type FetchBuildDataParams = FetchBuildDataArg & {
  inBackground?: boolean
  forceRefetch?: boolean
  withPager?: boolean
  page?: number
}
type LoadBuildsDataParams = FetchBuildDataParams & {
  isPreload?: boolean
}
export const loadBuildsData =
  ({
    locator,
    withPager = false,
    page,
    requestOptions,
    extraRequestOptions,
    inBackground = false,
    forceRefetch = false,
    isPreload = false,
  }: LoadBuildsDataParams): AppThunk<LoadedEndpoints> =>
  (dispatch, getState) => {
    const state = getState()
    const pager = getPager(state, PagerGroup.BUILD)
    const initiateOptions = {forceRefetch, subscribe: !isPreload}
    return withPager
      ? dispatch(
          fetchBuildsPageData(
            {
              locator,
              pager,
              page,
              requestOptions,
              extraRequestOptions,
              inBackground,
            },
            initiateOptions,
          ),
        )
      : dispatch(
          fetchBuildsAllData(
            locator,
            requestOptions,
            inBackground,
            initiateOptions,
            extraRequestOptions,
          ),
        )
  }

export const fetchBuildsData =
  (params: FetchBuildDataParams): AppThunk<Refetchable> =>
  dispatch =>
    combineRefetchables(
      dispatch(loadBuildsData(params))
        .filter(truthy)
        .map(item => item.result)
        .filter(notNull),
    )
